import { Injectable } from "@angular/core";
import { MassUploadEntryConfig, MassUploadEntryMap, MassUploadExcelToJsonMap, MassUploadStateFormMap, EXCEL_PERSON_TYPES, EXCEL_ROW_INDEX, EXCEL_ENROLLMENT_TYPES, MassUploadRowData, MassUploadSpouseDependentValidations } from "../interfaces/mass-upload-grid.interface";
import { MassUploadGridService } from "./mass-upload-grid.service";
import moment from "moment";
import { UntypedFormControl, UntypedFormGroup } from "@angular/forms";
import { EntryConfigInterface, FormConfig, FormNavigationConfig, PtlMaskService } from "@ptl/forms";
import { MASS_UPLOAD_STATE_NAME } from "../reducer/mass-upload-grid.reducer";
import { MOMENT_PARSE_FORMAT } from "../interfaces/mug-submission.interface";
import { MassUploadGridEntryMapTransformerService } from "./mug-entry-map-transformer.service";


@Injectable({
  providedIn: 'root'
})
export class MassUploadGridFormHelperService {

  gridFormMap: MassUploadStateFormMap[] = [];
  formJsonToExcelMap: MassUploadExcelToJsonMap[] = [];

  openSpouseValidations: MassUploadSpouseDependentValidations = {minAge: 18, maxAge: 124, maxSpousalPartners: 1, medicareEligibleAge: 65, maxChildOnlyAge: 21};
  openDependentValidations: MassUploadSpouseDependentValidations = {minAge: 0, maxAge: 26, maxDependents: 1, minSmokingAge: 21, maxChildOnlyAge: 21};
  specialSpouseValidations: MassUploadSpouseDependentValidations = {minAge: 18, maxAge: 124, maxSpousalPartners: 1, medicareEligibleAge: 65, maxChildOnlyAge: 21};
  specialDependentValidations: MassUploadSpouseDependentValidations = {minAge: 0, maxAge: 26, maxDependents: 1, minSmokingAge: 21, maxChildOnlyAge: 21};

  constructor(
    private entryTransformerService: MassUploadGridEntryMapTransformerService,
    private ptlMaskService: PtlMaskService
  ) {
    
  }

  getDependentValidations(enrollmentType: EXCEL_ENROLLMENT_TYPES): MassUploadSpouseDependentValidations {
    return enrollmentType === EXCEL_ENROLLMENT_TYPES.OEP ? this.openDependentValidations : this.specialDependentValidations;
  }

  getSpouseValidations(enrollmentType: EXCEL_ENROLLMENT_TYPES): MassUploadSpouseDependentValidations {
    return enrollmentType === EXCEL_ENROLLMENT_TYPES.OEP ? this.openSpouseValidations : this.specialSpouseValidations;
  }
  
  configureEnrollmentMap(allEntryMap: MassUploadEntryMap, formEntryOverrides: MassUploadEntryConfig[]) {
    this.entryTransformerService.addMassUploadAttributesToAllEntries(allEntryMap);
    this.entryTransformerService.addCustomFormEntries(allEntryMap, formEntryOverrides);
  }

  loadDependentValidations(openEnrollmentAboutYouJson: FormConfig<FormNavigationConfig>, specialEnrollmentAboutYouJson: FormConfig<FormNavigationConfig>) {
    let oepAboutYouMap = this.entryTransformerService.flattenAllEntriesToMap(openEnrollmentAboutYouJson);
    let oepSpouseValidations = oepAboutYouMap['spousal-partner']?.custom;
    let oepDependentValidations = oepAboutYouMap.dependents?.custom;
    Object.assign(this.openSpouseValidations, oepSpouseValidations);
    Object.assign(this.openDependentValidations, oepDependentValidations);
    
    let sepAboutYouMap = this.entryTransformerService.flattenAllEntriesToMap(specialEnrollmentAboutYouJson);
    let sepSpouseValidations = sepAboutYouMap['spousal-partner']?.custom;
    let sepDependentValidations = sepAboutYouMap.dependents?.custom;
    Object.assign(this.specialSpouseValidations, sepSpouseValidations);
    Object.assign(this.specialDependentValidations, sepDependentValidations);
  }

  toFormGroup(entries: EntryConfigInterface[]) {
    const formGroupByPath: {[key:string]: {[key:string]: UntypedFormControl}} = {};

    entries.forEach((entry: EntryConfigInterface) => {
      const connectPath = entry.formControlAttributes?.connectPath;

      if (connectPath) {
        formGroupByPath[connectPath] = formGroupByPath[connectPath] ? formGroupByPath[connectPath] : {};
        const disabled = entry.formControlAttributes && entry.formControlAttributes.disabled;

        if (entry.base.componentType === 'date-input') {
          formGroupByPath[connectPath][entry.formControlAttributes.formControlName] =
          new UntypedFormControl({ value: moment(entry.formControlAttributes.setDefaultValue, MOMENT_PARSE_FORMAT), disabled: disabled });
        } 
        else {
          formGroupByPath[connectPath][entry.formControlAttributes.formControlName] =
          new UntypedFormControl({ value: entry.formControlAttributes.setDefaultValue, disabled: disabled });
        }
      }

    });

    const formGroupResult: {[key:string]: UntypedFormGroup} = {};

    for (const formGroup in formGroupByPath) {
      if (formGroupByPath.hasOwnProperty(formGroup)) {
        formGroupResult[formGroup] = new UntypedFormGroup(formGroupByPath[formGroup]);
      }
    }

    return formGroupResult;
  }

  
  /**
   * 
   * EXCEL INTAKE
   * 
   * This is where the mass-upload-grid.json COMPARES against the EXCEL DATA
   * ( this is where we first lost values from excel when there is shared formControlNames but different connect paths: producerFullName: { firstName } will overwrite "firstName" because producerFullName.firstName will be hoisted )
   * 
   * Scenario 1:
   *    FORMS-AS-JSON is used to generate a flat structure based on formControlName: [ { page1entry1 }, { page1entry2 }, { page2entry1 } ]
   *    FORMS-AS-JSON loses data when entries have shared names, albeit different connect paths: [ { page1entry1name:{ firstName: null } }, { page50entry69name:{ firstName: "ted" } } } ]
   *      In this case, page50entry69name.firstName will be read later, and its value will overwrite page1entry1name.firstName
   * 
   * 
   * @param allEntryMap -
   * @param newRowData - contains array of excel cell data for this row
   * @param entryStateId - 
   * @returns 
   */
  buildSingleRowEntryList(allEntryMap: MassUploadEntryMap, newRowData: MassUploadRowData, entryStateId: number): MassUploadEntryConfig[] {
    const entryList: MassUploadEntryConfig[] = [];

    this.formJsonToExcelMap.forEach((mapItem: MassUploadExcelToJsonMap, mapIndex: number) => {

      // Some forms may be incomplete and be missing entry definitions. In this case we add a blank disabled control as a placeholder
      if (!allEntryMap[mapItem.formControlName]) {
        entryList.push(this.entryTransformerService.getPlaceholderEntryConfig(mapItem.formControlName));
        return;
      }

      // Create a copy of the main form config entry so we can customize it
      let entryCopy: MassUploadEntryConfig = JSON.parse(JSON.stringify(allEntryMap[mapItem.formControlName]));

      // Set the default value based on either excel data or service data
      entryCopy.formControlAttributes.setDefaultValue = this.getDefaultValue(newRowData, mapItem, mapIndex);
      this.autoFormatEntryValues(entryCopy, mapItem);

      // This order is important due to how the listeners are initalized and updated
      this.setDefaultFormControlEntryFields(entryCopy, mapItem, entryStateId);
      this.entryTransformerService.setCustomListeners(entryCopy, mapItem, entryStateId);
      this.configureDependentFields(newRowData, entryCopy, mapItem);
      
      entryList.push(entryCopy);
    });

    return entryList;
  }

  // Setup dependent/spouse specific fields and validations
  configureDependentFields(newRowData: MassUploadRowData, entryCopy: MassUploadEntryConfig, mapItem: MassUploadExcelToJsonMap) {
    const dataRow = newRowData.rowData;
    const personType = dataRow[EXCEL_ROW_INDEX.PERSON_TYPE];
    const isSpouse = personType === EXCEL_PERSON_TYPES.SPOUSE;
    const isDependent = personType === EXCEL_PERSON_TYPES.DEPENDENT;
    const isChildOnly = personType === EXCEL_PERSON_TYPES.CHILD_ONLY;
    const isOpenEnrollment = dataRow[EXCEL_ROW_INDEX.ENROLLMENT_TYPE] === EXCEL_ENROLLMENT_TYPES.OEP;

    if (!isDependent && !isSpouse && !isChildOnly) {
      return;
    }

    // Get this specific enrollment type and dependent/spouse validations
    let validations = null;
    if (isChildOnly) {
      validations = isOpenEnrollment ? this.openDependentValidations : this.specialDependentValidations;
      if (mapItem.formControlName === 'dob') {
        entryCopy.formControlAttributes.validators = [
          {type: 'required', value: true},
          {type: 'maxLength', value: 10},
          {type: 'minAgeManual', value: validations.minAge},
          {type: 'maxAgeManual', value: validations.maxChildOnlyAge}
        ];
        entryCopy.custom = {
          minAge: validations.minAge,
          maxAge: validations.maxChildOnlyAge
        }
      }

      return;
    }
    else if (isDependent) {
      validations = isOpenEnrollment ? this.openDependentValidations : this.specialDependentValidations;
    }
    else if (isSpouse) {
      validations = isOpenEnrollment ? this.openSpouseValidations : this.specialSpouseValidations;
    }

    this.entryTransformerService.setSpouseAndDependentValidations(entryCopy, mapItem, validations, personType);
  }

  /**
   * @description 
      Get the cell value from the provided data.
      There can be a situation where the submission info does not match the form control exactly.
      The json-to-excel mapping config can define different control names for the data from the remote objects
   */
  getDefaultValue(newRowData: MassUploadRowData, mapItem: MassUploadExcelToJsonMap, mapIndex: number) {
    const dataRow = newRowData.rowData;
    let cellValue = dataRow[mapIndex] == '' ? null : dataRow[mapIndex];

    // trim whitespace for value matching later
    if (typeof cellValue === 'string') {
      cellValue = cellValue.trim();
    }


    /** 
     * "formControlName" and "importToFormControl" act as an "app-defined":"payload-required" naming convention
     */

    // Data coming from XHR response
    // ex. "otherLanguageSpoken" -> "otherLanguageSpoken"
    if (newRowData.formPatchData?.[mapItem.formControlName] != null) {
      cellValue = newRowData.formPatchData[mapItem.formControlName];
    }

    // Data coming from XHR response - but needs mapping
    // ex. "otherHealthPlanP&G" -> "hasOtherHealthPlan" ( XHR doesn't want to use ampersand )
    else if (newRowData.formPatchData?.[mapItem.importToFormControl] != null) {
      cellValue = newRowData.formPatchData[mapItem.importToFormControl];
    }

    // Map the form control value to the correct item in the value map
    // Data coming from the EXCEL SHEET and mapped against MASS-UPLOAD-GRID
    // ex. "gender":"M" -> "gender":"Male"
    if (mapItem.valueMapArray && cellValue != null && cellValue != '') {
      outerLoop: for (let objIndex in mapItem.valueMapArray) {
        for (let valueIndex in mapItem.valueMapArray[objIndex].excelValues)
          if (mapItem.valueMapArray[objIndex].excelValues.includes(cellValue)) {
            cellValue = mapItem.valueMapArray[objIndex].jsonValue;
            break outerLoop;
          }
      }
    }

    return cellValue;
  }

  setDefaultFormControlEntryFields(entryCopy: MassUploadEntryConfig, mapItem: MassUploadExcelToJsonMap, stateId: number) {
    entryCopy.massUploadAttributes.stateId = stateId;
    entryCopy.massUploadAttributes.valueMapArray = mapItem.valueMapArray;
    entryCopy.formControlAttributes.getValueFromState = null;
    entryCopy.formControlAttributes.disabled = mapItem.forceDisabled ?? false;
    entryCopy.base.display = true;
    entryCopy.formControlAttributes.connectPath = `${MASS_UPLOAD_STATE_NAME}.${stateId}.${entryCopy.formControlAttributes.connectPath}`;

    // Stash the actual component type of this control for customization later, and use the specialized grid control for display/processing
    entryCopy.massUploadAttributes.actualComponentType = entryCopy.base.componentType;

    // MUG-specific component variant
    if (mapItem.customComponentType) {
      entryCopy.base.componentType = mapItem.customComponentType;
    } else {
      entryCopy.base.componentType = 'mass-upload-grid-control';
    }

    // The helperId prevents form controls from listening to controls outside of its own form
    // There may be several forms operating at one time on load, and we don't want to cross pollute the streams  
    entryCopy.formControlAttributes.helperId = stateId;
    entryCopy.base.formListeners?.forEach((listener) => {
      listener.helperId = stateId;
    });
  }

  autoFormatEntryValues(entryCopy: MassUploadEntryConfig, mapItem: MassUploadExcelToJsonMap) {
    let maskValidator = entryCopy.formControlAttributes.validators?.find((validator) => validator.type === 'mask');
    if (!maskValidator?.pattern?.toString()) {
      return;
    }

    let value = entryCopy.formControlAttributes.setDefaultValue?.toString();
    let newValue = value;
    let maskValue = maskValidator.pattern.toString();

    if (this.ptlMaskService.isPhoneMask(maskValue)) {
      newValue = this.ptlMaskService.onPhoneInputChange(value);
    }
    else if (this.ptlMaskService.isDateMask(maskValue)) {
      newValue = this.ptlMaskService.onDateInputChange(value);
    }
    else if (this.ptlMaskService.isSSNMask(maskValue)) {
      newValue = this.ptlMaskService.onSsnInputChange(value);
    } 
    else if (this.ptlMaskService.isThirdPartyPayerIdentificationNumber(maskValue)) {
      newValue = this.ptlMaskService.onthirdPartyPayerIdentificationNumberInputChange(value);
    }

    entryCopy.formControlAttributes.setDefaultValue = newValue;
  }
}